/**
 * ChestShop sales tax.
 *
 * From the Redmont announcement, section 13:
 *
 *   (1) A chestshop sales tax of 2.5% is imposed on every chestshop
 *       transaction.
 *   (2) The tax is calculated on the sale price of the transaction and is
 *       withheld from the proceeds paid to the shop owner.
 *   (3) The rate may be changed only by an Act of Congress.
 *   (4) All chestshop sales tax revenue is directed to the DCGovernment
 *       account.
 *
 * Withheld, not added: a sign reading $12.00 still takes $12.00 off the buyer,
 * and the owner banks $11.70. So the sign price is the gross, and every "what
 * did I actually earn" figure has to come down by the tax - which is why this
 * lives in shared rather than in whichever page happened to need it first.
 *
 * (3) is the reason the rate is a named export rather than a literal spread
 * through the code: when Congress moves it, one line changes.
 */
export const SALES_TAX_RATE = 0.025;
/**
 * The tax withheld on a gross sale amount, to the cent.
 *
 * Rounded because it is money and because binary floats cannot hold 2.5% of
 * $12 - the raw product is 0.30000000000000004, which compares unequal to the
 * $0.30 the server itself reports withholding.
 */
export function salesTaxOn(gross) {
    if (!Number.isFinite(gross) || gross <= 0)
        return 0;
    return Math.round(gross * SALES_TAX_RATE * 100) / 100;
}
/**
 * The tax withheld, unrounded.
 *
 * For display where a cent is too coarse to be honest. A sign selling 64 for
 * $9.99 owes $0.2498, and the per-unit share is $0.0039 - both vanish to
 * $0.00 if rounded to cents before they reach the screen. Totals still use
 * salesTaxOn, because money owed is settled in cents.
 */
export function salesTaxExact(gross) {
    if (!Number.isFinite(gross) || gross <= 0)
        return 0;
    return gross * SALES_TAX_RATE;
}
/** What the owner actually banks on a gross sale amount, to the cent. */
export function netOfTax(gross) {
    if (!Number.isFinite(gross) || gross <= 0)
        return 0;
    return Math.round((gross - salesTaxOn(gross)) * 100) / 100;
}
/**
 * Splits a gross figure into what the owner keeps and what is withheld.
 *
 * Rounded to cents, and the two are made to add back up to the gross rather
 * than each being rounded on its own - otherwise a page showing gross, net and
 * tax side by side can show three numbers that do not reconcile.
 */
export function splitTax(gross) {
    if (!Number.isFinite(gross) || gross <= 0)
        return { gross: 0, tax: 0, net: 0 };
    const rounded = Math.round(gross * 100) / 100;
    const tax = salesTaxOn(gross);
    return { gross: rounded, tax, net: Math.round((rounded - tax) * 100) / 100 };
}
//# sourceMappingURL=tax.js.map